import { useEffect, useState } from 'react'
import { sendGet } from 'hooks/use-fetch'
import {
  Button,
  Typography,
  Stack,
  Skeleton,
  Box
} from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import DateRangeIcon from '@mui/icons-material/DateRange'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import { useNavigate } from 'react-router-dom'
import { BirthdayToAgeInYearsAndMonths } from '../../core/Utilities'
import S3Avatar from '../S3Image/S3Avatar'
import { AsYouType } from 'libphonenumber-js'

interface Props {
  childId: number
  caregiverId: number
  hideEditButton?: boolean
  cardCaption?: string
}

interface CaregiverDetailsModel {
  firstName: string
  lastName: string
  relationship: string
  isFinanciallyResponsible: boolean
  livesWithChild: boolean
  primaryLanguage: string
  phone: string
  email: string
  dateOfBirth: Date | null
  address: string
  city: string
  state: string
  zip: string
  childFirstName: string
  s3Key: string | null
}

const CaregiverDetails = (props: Props): JSX.Element => {
  const nav = useNavigate()
  const [caregiver, setCaregiver] = useState<CaregiverDetailsModel | null>(null)

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response, success } = await sendGet(
        `/CaregiverDetails/GetDetails?childId=${props.childId}&caregiverId=${props.caregiverId}`, {})

      success && setCaregiver(response.response)
    }

    void fetchData()
  }, [props.childId, props.caregiverId])

  if (caregiver == null) { return <Skeleton /> }
  return <Box className='CardDisplay'>
    <Typography variant='h6' sx={{ fontWeight: 'bold' }} gutterBottom>{props.cardCaption ?? 'Caregiver Details'}</Typography>
    <Stack direction='column' spacing={1}>
      <Stack direction='row' spacing={1}>
        {caregiver?.s3Key !== null && caregiver?.s3Key != null ? <S3Avatar imageKey={caregiver?.s3Key} height='30px' width='30px' /> : <PersonIcon />}
        <Stack direction='column'>
          <Typography variant='body1' sx={{ fontWeight: 'bold', overflowWrap: 'break-word' }}>
            {`${caregiver?.firstName ?? ''} ${caregiver?.lastName ?? ''}`}
          </Typography>
          {caregiver?.relationship != null && <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {caregiver?.relationship}
          </Typography>}
          {caregiver?.isFinanciallyResponsible != null && <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {caregiver?.isFinanciallyResponsible ? `Financially Responsible for ${caregiver?.childFirstName}` : `Not Financially Responsible for ${caregiver?.childFirstName}`}
          </Typography>}
          {caregiver?.livesWithChild != null && <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {caregiver?.livesWithChild ? `Lives With ${caregiver?.childFirstName}` : `Does Not Live With ${caregiver?.childFirstName}`}
          </Typography>}
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {`Speaks ${caregiver?.primaryLanguage ?? 'English'}`}
          </Typography>
        </Stack>
      </Stack>
      {(caregiver?.phone ?? '') !== '' && <Stack direction='row' spacing={1}>
        <PhoneIcon />
        <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
          {new AsYouType('US').input(caregiver.phone ?? '')}
        </Typography>
      </Stack>}
      {(caregiver?.email ?? '') !== '' && <Stack direction='row' spacing={1}>
        <EmailIcon />
        <Typography variant='body1' sx={{ overflowWrap: 'anywhere' }}>
          {<a href={`mailto:${caregiver?.email ?? ''}`}>
            {caregiver?.email}
          </a>}
        </Typography>
      </Stack>}
      {caregiver?.dateOfBirth != null && caregiver?.dateOfBirth !== null && <Stack direction='row' spacing={1}>
        <DateRangeIcon />
        <Stack direction='row' spacing={1} flexWrap='wrap' alignItems='center'>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {(caregiver?.dateOfBirth !== null && caregiver?.dateOfBirth != null && new Date(caregiver?.dateOfBirth).toLocaleString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            }))}
          </Typography>
          {caregiver !== null && <Typography variant='body2' sx={{ overflowWrap: 'break-word' }}>
            ({BirthdayToAgeInYearsAndMonths(caregiver.dateOfBirth, false)})
          </Typography>}
        </Stack>
      </Stack>}
      {caregiver?.address != null && <Stack direction='row' spacing={1}>
        <PlaceOutlinedIcon />
        <Stack direction='column'>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {caregiver?.address}
          </Typography>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {`${caregiver?.city ?? ''}, ${caregiver?.state ?? ''}`}
          </Typography>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {caregiver?.zip}
          </Typography>
        </Stack>
      </Stack>}
      {!(props.hideEditButton ?? false) && <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant='contained'
          onClick={() => {
            nav(`/CaregiverDetails/Edit/${props.caregiverId}/Child/${props.childId}`)
          }}
          sx={{ textTransform: 'none', px: 4 }}
        >
          Edit Caregiver
        </Button>
      </Box>}
    </Stack>
  </Box>
}

export default CaregiverDetails
